import React, { useState, useEffect, useContext } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Grid,
  List,
  IconButton,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Stack,
  Badge,
  Fab,
  TextField,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Box,
  Chip,
  Tooltip
} from "@mui/material";

import { listInvoices } from "../../graphql/queries";
import { createInvoice, deleteInvoice } from "../../graphql/mutations";
import FolderIcon from "@mui/icons-material/Folder";
import InvoiceIcon from "@mui/icons-material/Receipt";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Menu from '@mui/material/Menu';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import InventoryIcon from '@mui/icons-material/Inventory';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';

import { AppContext } from "../../App"; // Import the AppContext from App.js

import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import OurLoader from "../OurLoader";
import { useUser } from "../../UserContext";

const listUsersCustom = `
  query ListUsers(
    $limit: Int,
    $filter: ModelUserFilterInput
  ) {
    listUsers(limit: $limit, filter: $filter) {
      items {
        id
        email
        companyName
      }
    }
  }
`;

const listInvoicesWithOwner = /* GraphQL */ `
  query ListInvoices(
    $filter: ModelInvoiceFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listInvoices(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        boxes {
          nextToken
        }
        dateSent
        ownerID
        owner {
          id
          username
          contactName
          companyName
          email
        }
        recipientID
        recipient {
          id
          username
          contactName
          companyName
          email
        }
        invoiceNumber
        status
        name
        partCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [hasOpenInvoices, setHasOpenInvoices] = useState(false);
  const { userRecord } = useContext(AppContext); // Get the user from the AppContext
  const { activeProvider } = useUser();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceName, setInvoiceName] = useState('');
  const [customers, setCustomers] = useState([]);
  
  // Sort and filter states
  const [sortOption, setSortOption] = useState('newest');
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [filterOption, setFilterOption] = useState('all');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Handle sort menu
  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  
  const handleSortChange = (option) => {
    setSortOption(option);
    handleSortMenuClose();
  };
  
  // Handle filter menu
  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };
  
  const handleFilterChange = (option) => {
    setFilterOption(option);
    handleFilterMenuClose();
  };

  const StyledBadge = styled(Badge)(({ theme, status }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: status === "open" ? "#44b700" : "#f50057",
      color: "#44b700",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      "&::after": {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        animation: "ripple 1.2s infinite ease-in-out",
        border: "1px solid currentColor",
        content: '""',
      },
    },
    "@keyframes ripple": {
      "0%": {
        transform: "scale(.8)",
        opacity: 1,
      },
      "100%": {
        transform: "scale(2.4)",
        opacity: 0,
      },
    },
  }));

  const getRelativeLink = (path, activeProvider) => {
    // Return direct paths without company prefix
    return path;
  };

  const confirmDeleteInvoice = (invoice, event) => {
    event.stopPropagation();
    setInvoiceToDelete(invoice);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteInvoice = async () => {
    try {
      if (!invoiceToDelete) return;
      
      const input = {
        id: invoiceToDelete.id,
      };

      console.log("deleting", invoiceToDelete.id);

      const response = await API.graphql(
        graphqlOperation(deleteInvoice, { input })
      );

      console.log("delete response", response);
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      
      console.log("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      console.log("Error deleting invoice", error);
    }
  };

  const handleEditInvoice = (invoice, event) => {
    event.stopPropagation();
    console.log('Editing invoice:', invoice.id);
    const editPath = getRelativeLink(`/view-invoice/${invoice.id}`, activeProvider);
    console.log('Navigating to:', editPath);
    navigate(editPath);
  };

  const getFilteredInvoices = () => {
    if (!invoices || !invoices.length) return [];
    
    let filtered = [...invoices];
    
    // Apply status filter
    if (filterOption === 'open') {
      filtered = filtered.filter(invoice => invoice.status === 'open');
    } else if (filterOption === 'closed') {
      filtered = filtered.filter(invoice => invoice.status === 'closed');
    } else if (filterOption === 'sent') {
      filtered = filtered.filter(invoice => invoice.ownerID === userRecord.id);
    } else if (filterOption === 'received') {
      filtered = filtered.filter(invoice => invoice.recipientID === userRecord.id);
    }
    
    // Apply sort
    switch (sortOption) {
      case 'newest':
        return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'nameAZ':
        return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'nameZA':
        return filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      case 'mostParts':
        return filtered.sort((a, b) => (b.partCount || 0) - (a.partCount || 0));
      default:
        return filtered;
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Filter to only show invoices owned by the current user
      const invoiceFilter = {
        ownerID: { eq: userRecord.id }
      };
      
      const response = await API.graphql(
        graphqlOperation(listInvoicesWithOwner, { 
          filter: invoiceFilter 
        })
      );
      
      setLoading(false);
      console.log("Invoice response:", response);
      const invoicesData = response.data.listInvoices.items;
      if (invoicesData) {
        // Sort invoices by date, newest first
        const sortedInvoices = [...invoicesData].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setInvoices(sortedInvoices);
      }
      const openInvoices = invoicesData.filter(
        (invoice) => invoice.status === "open"
      );
      setHasOpenInvoices(openInvoices.length > 0);
    } catch (error) {
      setLoading(false);
      console.log("Error fetching invoices:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch pricelists owned by the current user
      const listPricelistsQuery = /* GraphQL */ `
        query ListPricelists($filter: ModelPricelistFilterInput) {
          listPricelists(filter: $filter, limit: 10000) {
            items { id }
          }
        }
      `;

      const plResp = await API.graphql(
        graphqlOperation(listPricelistsQuery, {
          filter: { 
            or: [
              { userId: { eq: userRecord.id } },
              { providerId: { eq: userRecord.id } }
            ]
          }
        })
      );

      const userPricelistIds = (plResp?.data?.listPricelists?.items || []).map(pl => pl.id);

      // Build OR filter for users similar to Users.js
      const usersQuery = /* GraphQL */ `
        query ListUsers($filter: ModelUserFilterInput, $limit: Int) {
          listUsers(filter: $filter, limit: $limit) {
            items { id email companyName contactName activePricelistID }
          }
        }
      `;

      const response = await API.graphql(
        graphqlOperation(usersQuery, {
          limit: 10000,
          filter: {
            or: [
              { createdByUserId: { eq: userRecord.id } },
              { priceListInvites: { contains: userRecord.id } },
              ...userPricelistIds.map(id => ({ activePricelistID: { eq: id } }))
            ]
          }
        })
      );
      setCustomers(response?.data?.listUsers?.items || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      if (!selectedCustomer) {
        console.error('No customer selected');
        return;
      }
      
      const input = {
        dateSent: selectedDate.toISOString(),
        ownerID: userRecord.id, // Current user is the owner
        recipientID: selectedCustomer,
        name: invoiceName,
        status: 'open',
        partCount: 0,
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}` // Generate a simple invoice number
      };

      const response = await API.graphql(graphqlOperation(createInvoice, { input }));
      console.log('Created invoice:', response);
      setOpenDialog(false);
      setInvoiceName('');
      setSelectedCustomer(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [userRecord]);

  if (loading) {
    return <OurLoader />;
  }

  // Get status color and icon
  const getStatusInfo = (status) => {
    if (status === 'open') {
      return { 
        color: 'success', 
        icon: <MoneyIcon fontSize="small" />,
        text: 'Open'
      };
    } else {
      return { 
        color: 'default', 
        icon: <CloseIcon fontSize="small" />,
        text: 'Closed'
      };
    }
  };

  // Helper to get display name for owner or recipient
  const getPersonDisplay = (person) => {
    if (!person) return "Unknown";
    if (person.companyName) return person.companyName;
    if (person.contactName) return person.contactName;
    if (person.username) return person.username;
    if (person.email) return person.email;
    return "Unknown";
  };

  return (
    <Grid item xs={12} md={6}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        my: 3, 
        px: 2,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        pl: 2,
        py: 1
      }}>
        <Box>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontFamily: "Poppins", fontWeight: 'bold' }}
          >
            Invoice Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoices.length} Total Invoices ({invoices.filter(inv => inv.ownerID === userRecord.id).length} Sent, {invoices.filter(inv => inv.recipientID === userRecord.id).length} Received)
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<FilterListIcon />}
            onClick={handleFilterMenuOpen}
            sx={{ height: 40 }}
          >
            Filter
          </Button>
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={handleFilterMenuClose}
          >
            <MenuItem 
              onClick={() => handleFilterChange('all')}
              selected={filterOption === 'all'}
            >
              All Invoices
            </MenuItem>
            <MenuItem 
              onClick={() => handleFilterChange('open')}
              selected={filterOption === 'open'}
            >
              Open Invoices
            </MenuItem>
            <MenuItem 
              onClick={() => handleFilterChange('closed')}
              selected={filterOption === 'closed'}
            >
              Closed Invoices
            </MenuItem>
            <MenuItem 
              onClick={() => handleFilterChange('sent')}
              selected={filterOption === 'sent'}
            >
              Sent Invoices
            </MenuItem>
            <MenuItem 
              onClick={() => handleFilterChange('received')}
              selected={filterOption === 'received'}
            >
              Received Invoices
            </MenuItem>
          </Menu>
          
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<SortIcon />}
            onClick={handleSortMenuOpen}
            sx={{ height: 40 }}
          >
            Sort
          </Button>
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={handleSortMenuClose}
          >
            <MenuItem 
              onClick={() => handleSortChange('newest')}
              selected={sortOption === 'newest'}
            >
              Newest First
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange('oldest')}
              selected={sortOption === 'oldest'}
            >
              Oldest First
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange('nameAZ')}
              selected={sortOption === 'nameAZ'}
            >
              Name (A-Z)
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange('nameZA')}
              selected={sortOption === 'nameZA'}
            >
              Name (Z-A)
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange('mostParts')}
              selected={sortOption === 'mostParts'}
            >
              Most Parts First
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Typography
        sx={{
          mb: 2,
          px: 2,
          color: "text.secondary",
        }}
        variant="body2"
      >
        Manage your invoices: view invoices you've sent to customers and received from suppliers. {invoices.length > 0 ? 
        'Blue-bordered invoices are ones you\'ve sent, orange-bordered are ones you\'ve received.' : ''}
      </Typography>

      <Box sx={{ px: 2, py: 1 }}>
        {getFilteredInvoices().length === 0 ? (
          <Card sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            border: '1px dashed rgba(0,0,0,0.2)',
            borderRadius: 2
          }}>
            <DescriptionIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 2 }}>
              No invoices found.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Create New Invoice
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {getFilteredInvoices().map((invoice) => {
              const statusInfo = getStatusInfo(invoice.status);
              return (
                <Grid item xs={12} sm={6} md={12} lg={6} key={invoice.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      },
                      borderLeft: `4px solid ${statusInfo.color === 'success' ? '#44b700' : theme.palette.grey[500]}`,
                      borderRight: invoice.ownerID === userRecord.id ? `4px solid ${theme.palette.info.main}` : 
                                   invoice.recipientID === userRecord.id ? `4px solid ${theme.palette.warning.main}` : 'none'
                    }}
                    onClick={() => navigate(getRelativeLink(`/view-invoice/${invoice.id}`, activeProvider))}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Avatar sx={{ bgcolor: invoice.ownerID === userRecord.id ? theme.palette.info.main : theme.palette.warning.main, mr: 2 }}>
                          <InvoiceIcon />
                        </Avatar>
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                            {invoice.name || 'Unnamed Invoice'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={invoice.ownerID === userRecord.id ? "Sent" : "Received"} 
                              color={invoice.ownerID === userRecord.id ? "info" : "warning"}
                              sx={{ mr: 1 }}
                            />
                            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(invoice.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                            {invoice.owner && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Owner: {getPersonDisplay(invoice.owner)}
                                </Typography>
                              </Box>
                            )}
                            {invoice.recipient && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Customer: {getPersonDisplay(invoice.recipient)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Chip 
                          icon={statusInfo.icon}
                          label={statusInfo.text}
                          color={statusInfo.color}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <InventoryIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
                          <Typography variant="body2">
                            <strong>{invoice.partCount || 0}</strong> Parts
                          </Typography>
                        </Box>
                        <CardActions sx={{ p: 0 }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(getRelativeLink(`/view-invoice/${invoice.id}`, activeProvider));
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Invoice">
                            <IconButton 
                              size="small"
                              color="info"
                              onClick={(e) => handleEditInvoice(invoice, e)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Invoice">
                            <IconButton 
                              size="small"
                              color="error"
                              onClick={(e) => confirmDeleteInvoice(invoice, e)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        )}
      </Box>

      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={() => setOpenDialog(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Invoice Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} />
            Create New Invoice
          </Box>
          <IconButton onClick={() => setOpenDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Invoice Name"
              value={invoiceName}
              onChange={(e) => setInvoiceName(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <TextField
              type="datetime-local"
              label="Invoice Date"
              value={selectedDate.toISOString().slice(0, 16)}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <TextField
              select
              label="Customer"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.companyName || customer.email}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            startIcon={<UndoIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateInvoice}
            disabled={!selectedCustomer || !invoiceName}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          color: theme.palette.error.main
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the invoice "{invoiceToDelete?.name || 'Unnamed Invoice'}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            startIcon={<UndoIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteInvoice} 
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Invoices;
